import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ClipboardCheck,
  ArrowRight,
  RotateCcw,
  ChevronDown,
  ShieldAlert,
  Info,
} from "lucide-react";
import {
  QUIZZES,
  QUIZ_CATEGORIES,
  type Quiz,
  type QuizOption,
} from "@/lib/quizzes-data";
import { THERAPISTS, type Therapist } from "@/lib/therapists-data";

export const Route = createFileRoute("/quiz")({
  head: () => ({
    meta: [
      { title: "Gentle Tests to Know Yourself — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Trauma-informed self-reflection tools. Attachment, nervous system, IFS, boundaries, self-compassion and more. Not diagnoses.",
      },
      { property: "og:title", content: "Gentle Tests to Know Yourself — Recovery Trauma™" },
      { property: "og:description", content: "Trauma-informed self-reflection tools. Attachment, nervous system, IFS, boundaries, self-compassion and more. Not diagnoses." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/quiz" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/quiz" }],
  }),
  component: QuizPage,
});

function QuizCard({ quiz, onStart }: { quiz: Quiz; onStart: () => void }) {
  return (
    <button
      type="button"
      onClick={onStart}
      className="group flex w-full flex-col rounded-2xl border border-border/60 bg-card p-6 text-start shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[var(--shadow-gold)]"
    >
      <h3 className="font-display text-xl text-foreground">{quiz.title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
        {quiz.blurb}
      </p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)]">
        Begin <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function QuizRunner({ quiz, onExit }: { quiz: Quiz; onExit: () => void }) {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState<Record<string, number>>({});

  function pick(opt: QuizOption) {
    const next = { ...scores };
    for (const [k, v] of Object.entries(opt.score)) {
      next[k] = (next[k] ?? 0) + v;
    }
    setScores(next);
    setStep((s) => s + 1);
  }

  if (step >= quiz.questions.length) {
    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const top = sorted[0]?.[0];
    const result = top ? quiz.results[top] : null;
    const totalScore = sorted.reduce((acc, [, v]) => acc + v, 0);
    const topShare = totalScore > 0 && sorted[0] ? Math.round((sorted[0][1] / totalScore) * 100) : 0;
    const confidence: "Soft" | "Steady" | "Strong" =
      topShare >= 55 ? "Strong" : topShare >= 35 ? "Steady" : "Soft";


    return (
      <QuizResult
        quiz={quiz}
        result={result}
        sorted={sorted}
        topShare={topShare}
        confidence={confidence}
        onRetake={() => {
          setStep(0);
          setScores({});
        }}
        onExit={onExit}
      />
    );
  }


  const q = quiz.questions[step];
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Question {step + 1} of {quiz.questions.length}
        </span>
        <button onClick={onExit} className="hover:text-foreground">
          Exit
        </button>
      </div>
      <div className="mb-6 h-1 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full bg-[var(--gold-deep)] transition-all"
          style={{ width: `${((step + 1) / quiz.questions.length) * 100}%` }}
        />
      </div>
      <h3 className="mb-5 font-display text-xl text-foreground">{q.q}</h3>
      <div className="space-y-2">
        {q.options.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => pick(opt)}
            className="flex w-full items-center justify-between rounded-xl border border-border bg-background px-4 py-3 text-start text-sm font-medium text-foreground transition-all hover:border-gold/40 hover:bg-gold/5"
          >
            <span>{opt.label}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}

function matchTherapists(quiz: Quiz, sorted: [string, number][]): Therapist[] {
  // Build a keyword pool from the quiz's top scoring categories + quiz topic.
  const topKeys = sorted.slice(0, 2).map(([k]) => k.toLowerCase());
  const pool = [quiz.title, quiz.blurb, quiz.category, ...topKeys]
    .join(" ")
    .toLowerCase();

  const scored = THERAPISTS.map((t) => {
    const haystack = [...t.modalities, ...t.specialties, t.bio, t.title]
      .join(" ")
      .toLowerCase();
    let score = 0;
    for (const term of [
      "trauma",
      "attachment",
      "nervous",
      "somatic",
      "ifs",
      "parts",
      "anxiety",
      "shame",
      "boundaries",
      "grief",
      "addiction",
      "self",
      "emotion",
      "couples",
      "identity",
    ]) {
      if (pool.includes(term) && haystack.includes(term)) score += 2;
    }
    for (const key of topKeys) {
      if (key && haystack.includes(key)) score += 3;
    }
    if (t.acceptingClients) score += 1;
    return { t, score };
  })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((x) => x.t);

  return scored;
}

function QuizResult({
  quiz,
  result,
  sorted,
  topShare,
  confidence,
  onRetake,
  onExit,
}: {
  quiz: Quiz;
  result: { title: string; description: string } | null;
  sorted: [string, number][];
  topShare: number;
  confidence: "Soft" | "Steady" | "Strong";
  onRetake: () => void;
  onExit: () => void;
}) {
  const [methodOpen, setMethodOpen] = useState(false);
  const matched = useMemo(() => matchTherapists(quiz, sorted), [quiz, sorted]);

  return (
    <div className="space-y-6">
      {/* Results summary */}
      <div className="rounded-2xl border border-gold/30 bg-gold-light/20 p-8 shadow-[var(--shadow-gold)]">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          Your reflection
        </div>
        <h2 className="font-display text-3xl text-foreground">
          {result?.title ?? "Thank you for reflecting."}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-foreground">
          {result?.description}
        </p>
        <p className="mt-6 rounded-lg border border-border bg-background/70 p-3 text-xs leading-relaxed text-muted-foreground">
          <strong className="font-semibold text-foreground">Important: </strong>
          This reflection is intended for informational and educational purposes
          only. It is not a substitute for professional medical advice,
          diagnosis, or treatment. Only a qualified healthcare professional can
          diagnose a mental health condition.
        </p>
      </div>

      {/* How We Reviewed Your Responses */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setMethodOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-3 px-6 py-4 text-start"
        >
          <span className="inline-flex items-center gap-2 font-display text-lg text-foreground">
            <Info className="h-4 w-4 text-[var(--gold-deep)]" />
            How We Reviewed Your Responses
          </span>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${methodOpen ? "rotate-180" : ""}`}
          />
        </button>
        {methodOpen && (
          <div className="space-y-4 border-t border-border/60 px-6 py-5 text-sm leading-relaxed text-muted-foreground">
            <p>
              Your reflection results are generated from the information you
              provided. Responses are evaluated using predefined scoring
              criteria and matching logic designed to identify areas where
              additional support may be beneficial.
            </p>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                Reflection reviewed
              </div>
              <p>
                <strong className="text-foreground">{quiz.title}</strong> —{" "}
                {quiz.category}
              </p>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                Scoring methodology
              </div>
              <p>
                Each answer maps to one or more pattern categories with a
                weighted score. After all questions, the category with the
                highest total score is surfaced as your primary reflection.
              </p>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                Match strength
              </div>
              <p>
                <strong className="text-foreground">{confidence}</strong>{" "}
                match — your top category represents about{" "}

                <strong className="text-foreground">{topShare}%</strong> of your
                total score.
              </p>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                Areas identified from your self-reported responses
              </div>
              <ul className="list-disc ps-5">
                {sorted.slice(0, 3).map(([k, v]) => (
                  <li key={k}>
                    <span className="text-foreground">{quiz.results[k]?.title ?? k}</span>{" "}
                    — score {v}
                  </li>
                ))}
              </ul>
            </div>
            <p className="rounded-lg border border-border bg-background/70 p-3 text-xs">
              This reflection is for informational purposes only and does not
              provide a medical diagnosis, clinical opinion, or treatment
              recommendation. Only a qualified healthcare professional can
              diagnose a mental health condition.
            </p>
          </div>
        )}
      </div>

      {/* Therapists Matching Your Preferences */}
      <section className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
        <h3 className="font-display text-2xl text-foreground">
          Therapists Matching Your Preferences
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Based on the information you provided, you can explore therapists
          whose specialties, services, and qualifications may be relevant to
          your needs.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {matched.map((t) => (
            <Link
              key={t.id}
              to="/therapists/$therapistId"
              params={{ therapistId: t.id }}
              className="group flex flex-col rounded-xl border border-border/60 bg-background p-4 transition-all hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[var(--shadow-gold)]"
            >
              {t.photo && (
                <img
                  src={t.photo}
                  alt={`Portrait of ${t.name}`}
                  loading="lazy"
                  width={200}
                  height={200}
                  className="mb-3 aspect-square w-full rounded-lg object-cover"
                />
              )}
              <div className="font-display text-base text-foreground">
                {t.name}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t.credentials}
              </div>
              <p className="mt-1 text-xs text-[var(--gold-deep)]">{t.title}</p>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Shown because their areas of practice may align with the
                concerns and preferences you shared.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]">
                View Full Profile <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link
            to="/therapists"
            className="btn-gold inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm"
          >
            Browse the full directory <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="mt-4 rounded-lg border border-border bg-background/70 p-3 text-xs leading-relaxed text-muted-foreground">
          Therapists displayed on this page are shown based on your preferences
          and reflection responses. These results do not constitute a clinical
          recommendation, referral, or endorsement. Completing this reflection
          does not establish a provider–patient relationship.
        </p>
      </section>

      {/* Crisis support */}
      <div className="rounded-2xl border border-border bg-background p-5 text-sm leading-relaxed text-foreground">
        <div className="mb-2 inline-flex items-center gap-2 font-semibold">
          <ShieldAlert className="h-4 w-4 text-[var(--gold-deep)]" />
          If you are in crisis
        </div>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">US:</strong> Call or text{" "}
            <strong className="text-foreground">988</strong> (Suicide &amp;
            Crisis Lifeline) or dial 911 in an emergency.
          </li>
          <li>
            <strong className="text-foreground">UK:</strong> Call{" "}
            <strong className="text-foreground">111</strong> for NHS urgent
            mental health support, the Samaritans on{" "}
            <strong className="text-foreground">116 123</strong>, or 999 in an
            emergency.
          </li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onRetake}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground hover:bg-secondary"
        >
          <RotateCcw className="h-4 w-4" /> Retake
        </button>
        <button
          type="button"
          onClick={onExit}
          className="btn-gold rounded-full px-5 py-2.5 text-sm"
        >
          Back to self-reflection tools
        </button>
      </div>
    </div>
  );
}

function QuizPage() {
  const [active, setActive] = useState<string | null>(null);
  const quiz = QUIZZES.find((q) => q.id === active) ?? null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <header className="mb-10 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <ClipboardCheck className="h-3.5 w-3.5" /> Self-Reflection Tools
        </div>
        <h1 className="font-display text-4xl tracking-tight text-foreground md:text-5xl">
          Gentle tests to know yourself
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
          These are reflective tools, not diagnoses. Take them slowly and notice
          what comes up in your body as you answer. The goal isn't to label
          yourself — it's to deepen self-awareness, understand your patterns,
          and support your recovery journey.
        </p>
      </header>

      {quiz ? (
        <div className="mx-auto max-w-3xl">
          <QuizRunner quiz={quiz} onExit={() => setActive(null)} />
        </div>
      ) : (
        <div className="space-y-10">
          {QUIZ_CATEGORIES.map((cat) => (
            <section key={cat}>
              <h2 className="mb-4 font-display text-2xl text-foreground">{cat}</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {QUIZZES.filter((q) => q.category === cat).map((q) => (
                  <QuizCard key={q.id} quiz={q} onStart={() => setActive(q.id)} />
                ))}
              </div>
            </section>
          ))}
          <p className="pt-4 text-center text-xs text-muted-foreground">
            None of these tools are diagnostic. If something surfaces that feels
            heavy, please reach out to a therapist or the support lines on the
            Resources page.
          </p>
        </div>
      )}
    </div>
  );
}
